import React, { useEffect, useMemo,useRef, useState } from "react";

import PropTypes from "prop-types";
import { Field } from "react-final-form";

import styles from "./FormDropDownField.module.scss";

const DropdownInner = ({
  input,
  meta,
  options,
  placeholder,
  disabled,
  searchable,
  isMulti,
  mapOption,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);

  // ✅ Normalize option
  const normalizeOption = (opt) => {
    if (!opt) return null;

    if (opt.value !== undefined && opt.label !== undefined) {
      return opt;
    }

    if (mapOption) return mapOption(opt);

    return {
      value: opt.value ?? opt.id,
      label:
        opt.label ??
        opt.name ??
        opt.first_name ??
        opt.email ??
        "Unnamed",
    };
  };

  // ✅ Normalize options
  const normalizedOptions = useMemo(() => {
    return (options || []).map(normalizeOption).filter(Boolean);
  }, [options]);

  // ✅ Selected values
  const selectedValues = useMemo(() => {
    if (isMulti) {
      return (input.value || [])
        .map((val) => {
          if (!val) return null;

          if (val.value !== undefined && val.label !== undefined) {
            return val;
          }

          return normalizeOption(val);
        })
        .filter(Boolean);
    }

    if (!input.value) return null;

    // 🔥 IMPORTANT: map primitive → object for display
    const match = normalizedOptions.find(
      (opt) => opt.value === input.value
    );

    return match || null;
  }, [input.value, normalizedOptions]);

  // 🔍 Filter
  const filteredOptions = useMemo(() => {
    if (!searchable) return normalizedOptions;

    return normalizedOptions.filter((opt) =>
      opt.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, normalizedOptions]);

  // ✅ Single select (FIXED)
  const handleSelectSingle = (option) => {
    input.onChange(option.value); // ✅ store ONLY value

    if (onChange) onChange(option);

    setIsOpen(false);
    setSearch("");
  };

  // ✅ Multi select
  const toggleOption = (option) => {
    const exists = selectedValues.some(
      (v) => v.value === option.value
    );

    let updated;

    if (exists) {
      updated = selectedValues.filter(
        (v) => v.value !== option.value
      );
    } else {
      updated = [...selectedValues, option];
    }

    input.onChange(updated);

    if (onChange) onChange(updated);
  };

  // ✅ Select all
  const isAllSelected =
    isMulti &&
    selectedValues.length === normalizedOptions.length &&
    normalizedOptions.length > 0;

  const handleSelectAll = () => {
    const updated = isAllSelected ? [] : normalizedOptions;

    input.onChange(updated);

    if (onChange) onChange(updated);
  };

  // 🧠 Display text
  const getDisplayText = () => {
    if (isMulti) {
      if (!selectedValues.length) return placeholder;

      if (selectedValues.length === 1)
        return selectedValues[0].label;

      return `${selectedValues.length} selected`;
    } else {
      return selectedValues?.label || placeholder;
    }
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className={styles.dropdownWrapper}>
      <div
        className={`${styles.input} ${
          meta.error && meta.touched ? styles.inputError : ""
        }`}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
      >
        {getDisplayText()}
      </div>

      {isOpen && (
        <div className={styles.dropdownMenu}>
          {searchable && (
            <input
              className={styles.searchInput}
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}

          {isMulti ? (
            <>
              <div className={styles.option} onClick={handleSelectAll}>
                <input type="checkbox" checked={isAllSelected} readOnly />
                <span style={{ marginLeft: 8, fontWeight: 600 }}>
                  Select All
                </span>
              </div>

              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const isChecked = selectedValues.some(
                    (v) => v.value === option.value
                  );

                  return (
                    <div
                      key={option.value}
                      className={styles.option}
                      onClick={() => toggleOption(option)}
                    >
                      <input type="checkbox" checked={isChecked} readOnly />
                      <span style={{ marginLeft: 8 }}>
                        {option.label}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className={styles.noOptions}>No options</div>
              )}
            </>
          ) : (
            <>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className={styles.option}
                    onClick={() => handleSelectSingle(option)}
                  >
                    {option.label}
                  </div>
                ))
              ) : (
                <div className={styles.noOptions}>No options</div>
              )}
            </>
          )}
        </div>
      )}

      {meta.error && meta.touched && (
        <div className={styles.errorMessage}>{meta.error}</div>
      )}
    </div>
  );
};

const FormDropdownField = ({
  name,
  label,
  options = [],
  placeholder = "Select...",
  validate,
  required,
  disabled,
  searchable = false,
  isMulti = false,
  mapOption,
  onChange,
}) => {
  return (
    <div className={styles.formGroup}>
      {label && (
        <label htmlFor={name}>
          {label}
          {required && <span className={styles.required}> *</span>}
        </label>
      )}

      <Field name={name} validate={validate}>
        {({ input, meta }) => (
          <DropdownInner
            input={input}
            meta={meta}
            options={options}
            placeholder={placeholder}
            disabled={disabled}
            searchable={searchable}
            isMulti={isMulti}
            mapOption={mapOption}
            onChange={onChange}
          />
        )}
      </Field>
    </div>
  );
};

FormDropdownField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  validate: PropTypes.func,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  searchable: PropTypes.bool,
  isMulti: PropTypes.bool,
  mapOption: PropTypes.func,
  onChange: PropTypes.func,
};

export default FormDropdownField;